import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import posthog from 'posthog-js';
import { BehaviorSubject, map, mergeMap, tap } from 'rxjs';
import { CoreItemType } from '../core-item/core-item';
import { CoreItemService } from '../core-item/core-item.service';
import { Goal } from '../goal/goal';
import { Objection } from '../objection/objection';
import { ObjectionService } from '../objection/objection.service';
import { OpenaiModel } from '../openai/openai';
import { OpenaiService } from '../openai/openai.service';
import { PostHogEvent } from '../posthog/posthog.event';
import { Product } from '../product/product';
import { getScenarioNamePrompt } from '../prompt/scenario/get-scenario-name.prompt';
import { Resume } from '../resume/resume';
import { SessionTemplate, SessionTemplateUnSafeSubData } from './session-template';

@Injectable({
  providedIn: 'root',
})
export class SessionTemplateService {
  private _changeSubject = new BehaviorSubject<SessionTemplate | undefined>(undefined);

  constructor(
    private coreItemService: CoreItemService,
    private openaiService: OpenaiService,
    private objectionService: ObjectionService
  ) {}

  getById$(id: string) {
    return this.coreItemService.getItemByAction$({ id }).pipe(map((c) => c as SessionTemplate));
  }

  update$(item: SessionTemplate) {
    posthog.capture(PostHogEvent.sessionTemplateUpdate);
    return this.coreItemService.updateCoreItem$(item).pipe(
      tap((data) => {
        this._changeSubject.next(data);
      })
    );
  }

  itemListByResumeId$(resumeId: string) {
    return this._changeSubject.asObservable().pipe(
      mergeMap((_) => {
        return this.coreItemService
          .getCoreItemList$({ coreTypeList: [CoreItemType.sessionTemplate], parentIdList: [resumeId] })
          .pipe(
            map((list) => {
              list.sort((a, b) => ((a.createdTime ?? DateTime.local()) < (b.createdTime ?? DateTime.local()) ? 1 : -1));
              return list as SessionTemplate[];
            })
          );
      })
    );
  }

  itemList$() {
    return this._changeSubject.asObservable().pipe(
      mergeMap((_) => {
        return this.coreItemService.getCoreItemList$({ coreTypeList: [CoreItemType.sessionTemplate] }).pipe(
          map((list) => {
            list.sort((a, b) => ((a.createdTime ?? DateTime.local()) < (b.createdTime ?? DateTime.local()) ? 1 : -1));
            return list as SessionTemplate[];
          })
        );
      })
    );
  }

  addWithoutTitle$(resume: Resume, goal: Goal, product: Product, unsafeSubdata: SessionTemplateUnSafeSubData) {
    const scenarioNamePrompt = getScenarioNamePrompt({
      resume,
      goal,
      product,
    });

    return this.openaiService
      .getAnswer$({
        messageList: [{ role: 'user', content: scenarioNamePrompt }],
        config: { openaiModel: OpenaiModel.gpt4 },
      })
      .pipe(
        mergeMap((result) => {
          return this.add$(result, unsafeSubdata);
        })
      );
  }

  add$(title: string, unsafeSubData: SessionTemplateUnSafeSubData) {
    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        addMetadataList: [
          {
            title,
            parentId: unsafeSubData.resumeId,
            coreType: CoreItemType.sessionTemplate,
            unsafeSubData,
          },
        ],
      })
      .pipe(
        map((arr) => {
          this._changeSubject.next(arr[0] as SessionTemplate);
          return arr[0] as SessionTemplate;
        })
      );
  }

  delete$(itemList: SessionTemplate[]) {
    const deleteMetadataList = itemList.map((c) => ({
      id: c.id,
      sk: c.sk,
    }));

    itemList.forEach((c) => {
      posthog.capture(PostHogEvent.sessionTemplateDelete, {
        template_id: c.id,
      });
    });

    return this.coreItemService
      .batchWriteMetadataAndRelationList$({
        deleteMetadataList,
      })
      .pipe(
        tap((_) => {
          this._changeSubject.next(undefined);
        })
      );
  }

  //#region objection
  getEmotionalObjectionList$(sessionTemplate: SessionTemplate) {
    return this.objectionService.itemList$().pipe(
      map((itemList) => {
        const result: Objection[] = [];
        sessionTemplate?.unsafeSubData?.emotionalObjectionIdList?.forEach((id) => {
          const item = itemList.find((item) => item.id === id);
          if (item) {
            result.push(item);
          }
        });

        return result;
      })
    );
  }

  getFactObjectionList$(sessionTemplate: SessionTemplate) {
    return this.objectionService.itemList$().pipe(
      map((itemList) => {
        const result: Objection[] = [];
        sessionTemplate?.unsafeSubData?.factObjectionIdList?.forEach((id) => {
          const item = itemList.find((item) => item.id === id);
          if (item) {
            result.push(item);
          }
        });

        return result;
      })
    );
  }
}
